import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { DataBagService } from '@/common/data-bag.service';

import {
	Currency,
	PaymentsTerm,
	PaymentKind,
	BankAccount,
	FilteredOrganization,
	Reservation,
	Nationality,
	UserData,
	DocumentKind,
	Result,
} from './reservation.model';
import { TypeValueFactoryService } from '@/common/type-value-factory.service';
import { CancellationStatuses, Status } from '@/common/enums/Status';
import { removeDuplicatesFromArray } from '@/_utils/remove-duplicates-from-array';
import { ComfortPackageDialogData } from '@oskar-frontend/comfort-package';
import { EmailType } from '@/common/enums/EmailType';
import { DocumentName } from '@/common/enums/DocumentName';

@Injectable({
	providedIn: 'root',
})
export class ReservationService {
	constructor(private readonly api: DataBagService) {}

	slotDragging = false;
	currency = null;
	totalPrice = null;
	currencies: Currency[] = [];
	nationalities: Nationality[] = [];
	paymentKinds: PaymentKind[] = [];
	incomingBankAccounts: BankAccount[] = [];
	organizations: FilteredOrganization[] = [];
	users: UserData[] = [];
	documentKinds: DocumentKind[] = [];
	showAllOffers = false;
	newPayer: UserData = null;
	comfortPackageData: ComfortPackageDialogData = null;

	reservation$: BehaviorSubject<Reservation> = new BehaviorSubject<Reservation>(
		TypeValueFactoryService.createReservationObject(),
	);

	loadCurrencies() {
		this.api.get('/currency/', ({ results }) => {
			this.currencies = results;
		});
	}

	get isReservationCancelled(): boolean {
		return CancellationStatuses.includes(this.reservation$.getValue().status as Status);
	}

	loadNationalities() {
		this.api.get('/profile/nationality/', ({ results }) => {
			this.nationalities = results;
		});
	}

	loadReservation(reservationId: number) {
		this.api.get(`/reservation/${reservationId}/`, (result) => {
			if (result) this.reservation$.next(result);
		});
	}

	getCurrencyCode(currencyId: number): string {
		return this.currencies && this.currencies.length ? this.currencies.find((v) => v.id === currencyId).code : '';
	}

	loadPaymentKinds() {
		this.api.get('/payment/kind/', (result) => {
			if (result) this.paymentKinds = result.results;
		});
	}

	loadIncomingBankAccounts(params: string): void {
		this.api.get(`/bank_account/?${params}`, (result: Result<BankAccount>) => {
			if (result.next) {
				this.loadIncomingBankAccounts(result.next.split('?')[1]);
			}
			if (result) {
				this.incomingBankAccounts = removeDuplicatesFromArray([
					...this.incomingBankAccounts,
					...result.results,
				]);
			}
		});
	}

	loadOrganizations(limit = '999') {
		this.api.get(`/organization/?limit=${limit}`, (result) => {
			if (result) this.organizations = result.results;
		});
	}

	loadUsers() {
		this.api.get('/profile/data_full/', (result) => {
			if (result) this.users = result.results;
		});
	}

	getUnit(payment_term: PaymentsTerm): string {
		return `${payment_term.countKind === 'P' ? '%' : ` ${this.getCurrencyCode(payment_term.currency)}`}`;
	}

	convertDateToNumber(date: Date): number {
		return new Date(date).getTime();
	}

	formatOwnerUser(reservation: Reservation) {
		if (!reservation) return null;

		return {
			...reservation.ownerUser,
			userConsent: reservation.ownerUser.userConsent.map((v) => {
				return {
					...v,
					consent: v.consent.id,
				};
			}),
			userContacts: reservation.ownerUser.userContacts.map((v) => {
				return { ...v, destiny: v.destiny.id };
			}),
			nationality: reservation.ownerUser.nationality ? reservation.ownerUser.nationality.id : null,
		};
	}

	convertDocumentName(v): string {
		switch (v.name) {
			case DocumentName.RESERVATION_CONFIRM:
				return 'Potwierdzenie rezerwacji dla agenta';
			case DocumentName.VOUCHER:
				return 'Voucher';
			case DocumentName.RESERVATION_AGREEMENT:
				return 'Umowa';
			case EmailType.RESERVATION_CANCEL_AGREEMENT:
				return 'MAIL: Potwierdzenie anulowania rezerwacji';
			case EmailType.RESERVATION_CHANGED_AGREEMENT:
				return 'MAIL: Potwierdzenie zmiany rezerwacji';
			case EmailType.RESERVATION_CANCEL:
				return 'MAIL: Anulacja rezerwacji';
			case EmailType.RESERVATION_PAYMENT_REMIND:
				return 'MAIL: Przypomnienie o płatności';
			case EmailType.RESERVATION_CONFIRM_MAIL:
				return 'MAIL: Potwierdzenie rezerwacji';
			case EmailType.RESERVATION_CONFIRM_MAIL_FOR_AGENT:
				return 'MAIL: Potwierdzenie rezerwacji dla agenta';
			case EmailType.DOK_TO_SGL_MAIL:
				return 'MAIL: Zmiana DOK na SGL';
			case EmailType.DOK_TO_SGL_MAIL_FOR_AGENT:
				return 'MAIL: Zmiana DOK na SGL - mail dla agenta';
			case EmailType.DEPARTURE_CITY_WITHDRAWN:
				return 'MAIL: Zmiana miasta wyjazdu';
			case EmailType.DEPARTURE_CITY_WITHDRAWN_FOR_AGENT:
				return 'MAIL: Zmiana miasta wyjazdu - mail dla agenta';
			default:
				return v.name;
		}
	}

	loadDocumentsKinds(): void {
		this.api.get('/document/kind/', (result) => {
			if (result) {
				result.forEach((v) => {
					v['displayName'] = this.convertDocumentName(v);
				});
				this.documentKinds = this.highestVersionKind(result);
			}
		});
	}

	highestVersionKind(objects: DocumentKind[]): DocumentKind[] {
		const uniqueNames = Array.from(new Set(objects.map((obj) => obj.name)));
		return uniqueNames.map((name) => {
			const filteredObjects = objects.filter((obj) => obj.name === name);
			const highestVersionObject = filteredObjects.reduce((prev, current) =>
				this.verCompare(current.version, prev.version) > 0 ? current : prev,
			);

			return highestVersionObject;
		});
	}

	verCompare(versionA: string, versionB: string): number {
		return versionA.localeCompare(versionB);
	}
}
